import { computed, reactive, watch, onMounted, onUnmounted } from '@nuxtjs/composition-api';
import { useRoute } from '@nuxtjs/composition-api';
import { StateInterface, UseUiStateInterface } from '~/composables/useUiState/useUiState';

const state = reactive<StateInterface>({
  isCartSidebarOpen: false,
  isWishlistSidebarOpen: false,
  isLoginModalOpen: false,
  isNewsletterModalOpen: false,
  isCategoryGridView: true,
  isWishlistGridView: true,
  isFilterSidebarOpen: false,
  isMobileMenuOpen: false,
  isAccounMobileOpen: false,
  isStoreOpen: false,
  isAddCartModalOpen: false, // Add new state for AddCartModal
  isSearchPopupOpen: false, // Add new state for SearchPopup
});

/**
 * Global store for managing UI state.
 *
 * See the {@link UseUiStateInterface} for a list of methods and values available in this composable.
 */
export function useUiState(): UseUiStateInterface {
  const route = useRoute();

  onMounted(() => {
    watch(() => route.value.path, () => {
      if (state.isCartSidebarOpen) {
        state.isCartSidebarOpen = false;
      }
      if (state.isMobileMenuOpen) {
        state.isMobileMenuOpen = false;
      }
      if (state.isAccounMobileOpen) {
        state.isAccounMobileOpen = false;
      }
      if (state.isLoginModalOpen) {
        state.isLoginModalOpen = false;
      }
    });
  });

  const toggleAccounMobile = (isOpen: boolean) => {
    if (state.isMobileMenuOpen) {
      state.isMobileMenuOpen = false;
    }
    state.isAccounMobileOpen = isOpen;
  };

  const toggleMobileMenu = () => {
    state.isMobileMenuOpen = !state.isMobileMenuOpen;
    if (state.isAccounMobileOpen) {
      state.isAccounMobileOpen = false;
    }
    if (state.isStoreOpen) {
      state.isStoreOpen = false;
    }
  };

  const toggleCartSidebar = () => {
    if (state.isMobileMenuOpen) {
      state.isMobileMenuOpen = false;
    }
    if (state.isAccounMobileOpen) {
      state.isAccounMobileOpen = false;
    }

    state.isCartSidebarOpen = !state.isCartSidebarOpen;
  };

  const toggleWishlistSidebar = () => {
    if (state.isMobileMenuOpen) {
      state.isMobileMenuOpen = false;
    }
    if (state.isAccounMobileOpen) {
      state.isAccounMobileOpen = false;
    }

    state.isWishlistSidebarOpen = !state.isWishlistSidebarOpen;
  };

  const toggleLoginModal = () => {
    if (state.isMobileMenuOpen) {
      state.isMobileMenuOpen = false;
    }
    if (state.isAccounMobileOpen) {
      state.isAccounMobileOpen = false;
    }

    state.isLoginModalOpen = !state.isLoginModalOpen;
  };

  const toggleNewsletterModal = () => {
    state.isNewsletterModalOpen = !state.isNewsletterModalOpen;
  };

  const changeToCategoryGridView = () => {
    state.isCategoryGridView = true;
  };
  const changeToCategoryListView = () => {
    state.isCategoryGridView = false;
  };

  const changeToWishlistGridView = () => {
    state.isWishlistGridView = true;
  };

  const changeToWishlistListView = () => {
    state.isWishlistGridView = false;
  };
  const toggleFilterSidebar = () => {
    state.isFilterSidebarOpen = !state.isFilterSidebarOpen;
  };

  // Add new toggle method for AddCartModal
  const toggleAddCartModal = () => {
    state.isAddCartModalOpen = !state.isAddCartModalOpen;
  };

  // Add new toggle method for SearchPopup
  const toggleSearchPopup = () => {
    state.isSearchPopupOpen = !state.isSearchPopupOpen;
  };

  const toggleStore = (isOpen?: boolean) => {
    if (state.isMobileMenuOpen) {
      state.isMobileMenuOpen = false;
    }
    // Only close account mobile if we're explicitly opening the store
    if (isOpen === true && state.isAccounMobileOpen) {
      state.isAccounMobileOpen = false;
    }
    
    state.isStoreOpen = isOpen !== undefined ? isOpen : !state.isStoreOpen;
  };

  return {
    isMobileMenuOpen: computed(() => state.isMobileMenuOpen),
    isCartSidebarOpen: computed(() => state.isCartSidebarOpen),
    isWishlistSidebarOpen: computed(() => state.isWishlistSidebarOpen),
    isLoginModalOpen: computed(() => state.isLoginModalOpen),
    isNewsletterModalOpen: computed(() => state.isNewsletterModalOpen),
    isCategoryGridView: computed(() => state.isCategoryGridView),
    isWishlistGridView: computed(() => state.isWishlistGridView),
    isFilterSidebarOpen: computed(() => state.isFilterSidebarOpen),
    isAccounMobileOpen: computed(() => state.isAccounMobileOpen),
    isStoreOpen: computed(() => state.isStoreOpen),
    isAddCartModalOpen: computed(() => state.isAddCartModalOpen), // Add computed property
    isSearchPopupOpen: computed(() => state.isSearchPopupOpen), // Add computed property
    toggleMobileMenu,
    toggleCartSidebar,
    toggleWishlistSidebar,
    toggleLoginModal,
    toggleNewsletterModal,
    changeToCategoryGridView,
    changeToCategoryListView,
    changeToWishlistGridView,
    changeToWishlistListView,
    toggleFilterSidebar,
    toggleAccounMobile,
    toggleStore,
    toggleAddCartModal, // Add the toggle method
    toggleSearchPopup, // Add the toggle method for SearchPopup
  };
}

export default useUiState;
export * from './useUiState';
